import api from '../utils/api';
import { SmartFilter } from '../types/filters';

class FilterService {
  // Use the api instance with auth interceptors
  private api = api;

  // Get all filters
  async getFilters(): Promise<SmartFilter[]> {
    try {
      const response = await this.api.get('/filters');
      return response.data.filters;
    } catch (error: any) {
      // Return empty array if endpoint unavailable to prevent UI errors
      if (error?.response?.status === 404) {
        return [];
      }
      throw error;
    }
  }

  // Get a single filter by ID
  async getFilter(id: string): Promise<SmartFilter> {
    try {
      const response = await this.api.get(`/filters/${id}`);
      return response.data.filter;
    } catch (error: any) {
      // Return mock filter if endpoint unavailable
      if (error?.response?.status === 404) {
        return {
          id,
          name: 'Unavailable Filter',
          description: 'This filter is currently unavailable',
          criteria: {
            fileTypes: [],
            tags: [],
            timeRange: null,
            sizeRange: null,
            modifiedBy: null
          },
          createdAt: new Date().toISOString(),
          matchCount: 0
        };
      }
      throw error;
    }
  }

  // Create a new filter
  async createFilter(filter: Omit<SmartFilter, 'id' | 'createdAt' | 'matchCount'>): Promise<SmartFilter> {
    try {
      const response = await this.api.post('/filters', filter);
      return response.data.filter;
    } catch (error: any) {
      // Return mock created filter if endpoint unavailable
      if (error?.response?.status === 404) {
        return {
          ...filter,
          id: 'temp-' + Date.now(),
          createdAt: new Date().toISOString(),
          matchCount: 0
        } as SmartFilter;
      }
      throw error;
    }
  }

  // Update existing filter
  async updateFilter(id: string, updates: Partial<SmartFilter>): Promise<SmartFilter> {
    try {
      const response = await this.api.put(`/filters/${id}`, updates);
      return response.data.filter;
    } catch (error: any) {
      // Return updated filter with mock data if endpoint unavailable
      if (error?.response?.status === 404) {
        return {
          id,
          ...updates,
          createdAt: new Date().toISOString(),
          matchCount: 0
        } as SmartFilter;
      }
      throw error;
    }
  }

  // Delete a filter
  async deleteFilter(id: string): Promise<void> {
    try {
      await this.api.delete(`/filters/${id}`);
    } catch (error: any) {
      // Silently succeed if endpoint unavailable
      if (error?.response?.status === 404) {
        return;
      }
      throw error;
    }
  }

  // Run a filter and get matching files
  async runFilter(filterId: string, options?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<{
    items: any[];
    total: number;
  }> {
    try {
      const response = await this.api.get(`/filters/${filterId}/run`, { params: options });
      return response.data;
    } catch (error: any) {
      // Return empty results if endpoint unavailable
      if (error?.response?.status === 404) {
        return {
          items: [],
          total: 0
        };
      }
      throw error;
    }
  }

  // Get available filter tags
  async getFilterTags(): Promise<string[]> {
    try {
      const response = await this.api.get('/filters/tags');
      return response.data.tags;
    } catch (error: any) {
      // Handle server errors gracefully
      // Return empty array if endpoint fails
      return [];
    }
  }

  // Create default filters for the current user
  async createDefaultFilters(): Promise<SmartFilter[]> {
    try {
      const response = await this.api.post('/filters/default');
      return response.data.filters;
    } catch (error: any) {
      // Return mock default filters if endpoint unavailable
      if (error?.response?.status === 404) {
        const defaultFilters: SmartFilter[] = [
          {
            id: 'default-recent',
            name: 'Recent Files',
            description: 'Files modified in the last 7 days',
            criteria: {
              timeRange: { value: 7, unit: 'days' },
              fileTypes: [],
              tags: [],
              sizeRange: null,
              modifiedBy: null
            },
            createdAt: new Date().toISOString(),
            matchCount: 0
          },
          {
            id: 'default-images',
            name: 'Images',
            description: 'All image files',
            criteria: {
              fileTypes: ['image'],
              tags: [],
              timeRange: null,
              sizeRange: null,
              modifiedBy: null
            },
            createdAt: new Date().toISOString(),
            matchCount: 0
          },
          {
            id: 'default-documents',
            name: 'Documents',
            description: 'All document files',
            criteria: {
              fileTypes: ['document'],
              tags: [],
              timeRange: null,
              sizeRange: null,
              modifiedBy: null
            },
            createdAt: new Date().toISOString(),
            matchCount: 0
          }
        ];
        return defaultFilters;
      }
      throw error;
    }
  }
}

const filterService = new FilterService();

export default filterService;